/**
 * Create-user dialog for the User Management page.
 *
 * Scope mirrors the backend `ADMIN_STYLE_ROLES` allowlist — only roles
 * that don't need a related profile row (admin/support/operation/...
 * plus optional `is_staff`/`is_superuser` users) are creatable here.
 * Users that need a store, document image, or emergency contact
 * (rider/driver/staff/packer/manager/...) continue to be created from
 * the existing Roster / Riders pages, which already collect those
 * fields. We surface that constraint inline so admins know where to go.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/Dialog';
import { ToggleChip } from '@/components/ui/Chip';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Switch } from '@/components/ui/Switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useAuth } from '@/auth/AuthProvider';
import { extractErrorMessage } from '@/api/client';
import { notify } from '@/lib/toast';
import { cn } from '@/lib/cn';
import {
  CREATABLE_ADMIN_STYLE_ROLES,
  useCreateInternalUser,
  type InternalUserCreatePayload,
} from '@/api/bi/internalUsers';

const ROLE_NORMAL = 'role';
const ROLE_DJANGO_STAFF = 'staff_django';
const ROLE_SUPERUSER = 'superuser';
type RoleMode = typeof ROLE_NORMAL | typeof ROLE_DJANGO_STAFF | typeof ROLE_SUPERUSER;

interface FormState {
  name: string;
  username: string;
  phone_number: string;
  email: string;
  roleMode: RoleMode;
  role: string;
  password: string;
  generate_temp_password: boolean;
  is_active: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  username: '',
  phone_number: '',
  email: '',
  roleMode: ROLE_NORMAL,
  role: 'admin',
  password: '',
  generate_temp_password: true,
  is_active: true,
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateUserDialog({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const { user: actor } = useAuth();
  const create = useCreateInternalUser();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [createdUserId, setCreatedUserId] = useState<number | null>(null);

  // Reset the form whenever the dialog opens so a stale draft never
  // shows. Keep `tempPassword` between transitions because we render
  // a follow-up dialog when the server returns one.
  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
    }
  }, [open]);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  const canSubmit = useMemo(() => {
    if (!form.name.trim()) return false;
    if (!form.username.trim() && !form.phone_number.trim()) return false;
    if (!form.generate_temp_password && form.password.length < 6) return false;
    return true;
  }, [form]);

  async function submit() {
    const payload: InternalUserCreatePayload = {
      name: form.name.trim(),
      username: form.username.trim() || null,
      phone_number: form.phone_number.trim() || null,
      email: form.email.trim() || null,
      is_active: form.is_active,
      generate_temp_password: form.generate_temp_password,
    };
    if (!form.generate_temp_password && form.password) {
      payload.password = form.password;
    }
    if (form.roleMode === ROLE_NORMAL) {
      payload.role = form.role;
    } else if (form.roleMode === ROLE_DJANGO_STAFF) {
      payload.is_staff = true;
    } else if (form.roleMode === ROLE_SUPERUSER) {
      payload.is_superuser = true;
      payload.is_staff = true;
    }

    try {
      const created = await create.mutateAsync(payload);
      notify.success(`Created ${created.name || created.username || `user #${created.id}`}`);
      if (created.temporary_password) {
        // Park the temp password in the follow-up modal — closing the
        // create dialog hides this one but reveals the reveal modal.
        setTempPassword(created.temporary_password);
        setCreatedUserId(created.id);
        onOpenChange(false);
      } else {
        onOpenChange(false);
        navigate(`/users/${created.id}`);
      }
    } catch (err) {
      notify.error(extractErrorMessage(err, 'Could not create user'));
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl">
          <DialogTitle className="text-base font-semibold">Create new user</DialogTitle>
          <DialogDescription className="mt-1 text-xs text-muted-foreground">
            Admin-style roles only. For riders, drivers, staff billers, packers, managers
            (and other roles that need a store / documents / emergency contact),
            use the <strong>Roster</strong> or <strong>Riders</strong> page instead.
          </DialogDescription>

          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <Field label="Full name" required>
              <Input
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                placeholder="e.g. Anita Sharma"
                autoFocus
              />
            </Field>
            <Field label="Account type" required hint="Drives the role / Django flags below.">
              <Select
                value={form.roleMode}
                onValueChange={(v) => update('roleMode', v as RoleMode)}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ROLE_NORMAL}>Role-based account</SelectItem>
                  <SelectItem value={ROLE_DJANGO_STAFF}>Django staff (no role)</SelectItem>
                  {actor?.is_superuser ? (
                    <SelectItem value={ROLE_SUPERUSER}>Superuser</SelectItem>
                  ) : null}
                </SelectContent>
              </Select>
            </Field>

            {form.roleMode === ROLE_NORMAL ? (
              <Field label="Role" required>
                <Select value={form.role} onValueChange={(v) => update('role', v)}>
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CREATABLE_ADMIN_STYLE_ROLES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            ) : (
              <Field label="Permissions">
                <div className="flex h-9 items-center gap-2">
                  {form.roleMode === ROLE_SUPERUSER ? (
                    <Badge tone="primary">superuser</Badge>
                  ) : (
                    <Badge tone="info">django staff</Badge>
                  )}
                </div>
              </Field>
            )}

            <Field label="Username" hint="Either username or phone is required.">
              <Input
                value={form.username}
                onChange={(e) => update('username', e.target.value)}
                placeholder="e.g. anita"
                autoComplete="off"
              />
            </Field>
            <Field label="Phone number">
              <Input
                value={form.phone_number}
                onChange={(e) => update('phone_number', e.target.value)}
                placeholder="e.g. 9800000000"
                autoComplete="off"
              />
            </Field>
            <Field label="Email">
              <Input
                type="email"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                placeholder="optional"
                autoComplete="off"
              />
            </Field>

            <Field
              label="Status"
              hint={form.is_active ? 'Can sign in immediately.' : 'Created but blocked from signing in.'}
            >
              <div className="flex h-9 items-center gap-2">
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(v) => update('is_active', v)}
                  aria-label="Account active"
                />
                <span className={cn('text-xs font-medium', form.is_active ? 'text-success' : 'text-danger')}>
                  {form.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </Field>
          </div>

          {/* Password block */}
          <div className="mt-4 rounded-md border border-border bg-surface p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Password
              </p>
              <ToggleChip
                active={form.generate_temp_password}
                icon={<Sparkles className="h-3 w-3" aria-hidden />}
                onClick={() => update('generate_temp_password', !form.generate_temp_password)}
              >
                {form.generate_temp_password ? 'Auto-generate' : 'Type manually'}
              </ToggleChip>
            </div>
            <div className="mt-2">
              {form.generate_temp_password ? (
                <p className="text-[11px] text-muted-foreground">
                  A temporary password will be generated and shown once after the user is created.
                  Share it through a secure channel — it is never stored in plaintext.
                </p>
              ) : (
                <Input
                  type="text"
                  value={form.password}
                  onChange={(e) => update('password', e.target.value)}
                  placeholder="Minimum 6 characters"
                  autoComplete="new-password"
                />
              )}
            </div>
          </div>

          <div className="mt-5 flex items-center justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void submit()}
              disabled={!canSubmit || create.isPending}
            >
              {create.isPending ? 'Creating…' : 'Create user'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Follow-up modal — only shown when the server returned a temp password. */}
      <Dialog
        open={tempPassword != null}
        onOpenChange={(o) => {
          if (!o) {
            const id = createdUserId;
            setTempPassword(null);
            setCreatedUserId(null);
            if (id != null) navigate(`/users/${id}`);
          }
        }}
      >
        <DialogContent>
          <DialogTitle className="text-base font-semibold">Temporary password issued</DialogTitle>
          <DialogDescription className="mt-1 text-xs text-muted-foreground">
            This is the only time the password will be shown. Copy it now and share
            it through a secure channel.
          </DialogDescription>
          <div className="mt-4 rounded-md border border-border bg-muted/40 p-3">
            <code className="select-all break-all font-mono text-sm">
              {tempPassword}
            </code>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                if (tempPassword && navigator.clipboard) {
                  void navigator.clipboard
                    .writeText(tempPassword)
                    .then(() => notify.success('Copied to clipboard'));
                }
              }}
            >
              Copy
            </Button>
            <Button
              onClick={() => {
                const id = createdUserId;
                setTempPassword(null);
                setCreatedUserId(null);
                if (id != null) navigate(`/users/${id}`);
              }}
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

